import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Permission, PermissionService, SuperAdminAccessService } from '@vdms-hq/auth';
import {
  QUARANTINE_ALL_ASSET_TYPES,
  QuarantineConfigRules,
  SupplierPatchRequest,
  SupplierPostRequest,
  SupplierService,
} from '@vdms-hq/api-contract';
import { DynamicFilterInput, DynamicFormGroupRendererComponent } from '@vdms-hq/dynamic-filters';
import { QuarantineService } from '@vdms-hq/quarantine';
import { E2eIdDirective, RefreshService, SuppliersListOption, ValueFormat } from '@vdms-hq/shared';
import { ToastService } from '@vdms-hq/toast';
import {
  ActionContextLess,
  ButtonColorsEnum,
  DataPresentationHeaderComponent,
  DialogPageHostDirective,
  MultipleDataPresentationComponent,
  MultipleViewConfiguration,
  UIConfirmationDialogService,
} from '@vdms-hq/ui';
import { combineLatest, concat, debounceTime, EMPTY, Observable, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { SuppliersDatasourceService } from '../../logic/services/suppliers-datasource.service';
import {
  SupplierEditDialogResult,
  SuppliersCreateEditDialogComponent,
} from '../suppliers-create-edit-dialog/suppliers-create-edit-dialog.component';
import { FilterType } from '@vdms-hq/fields';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'vdms-hq-suppliers-suppliers-table',
  templateUrl: './suppliers-table.component.html',
  standalone: true,
  imports: [
    DataPresentationHeaderComponent,
    MultipleDataPresentationComponent,
    DynamicFormGroupRendererComponent,
    AsyncPipe,
    E2eIdDirective,
    DialogPageHostDirective,
  ],
  styleUrl: './suppliers-table.component.scss',
})
export class SuppliersTableComponent implements OnInit, OnDestroy {
  /** Scoped MatDialog, so the editor covers this component rather than the viewport (#16593). */
  @ViewChild(DialogPageHostDirective, { static: true }) private dialogPageHost!: DialogPageHostDirective;
  private quarantineService = inject(QuarantineService);
  private refreshService = inject(RefreshService);
  public readonly matDialog = inject(MatDialog);
  public suppliersDataSource = inject(SuppliersDatasourceService);
  public supplierService = inject(SupplierService);
  public toastService = inject(ToastService);
  public permissionService = inject(PermissionService);
  private deleteDialog = inject(UIConfirmationDialogService);
  private superAdminAccessService = inject(SuperAdminAccessService);

  enabled = [
    'name',
    'emails',
    'facility_of_origin',
    'is_global',
    'created_by',
    'created_at',
    'modified_by',
    'modified_at',
    'actions',
  ];

  viewConfiguration$!: Observable<MultipleViewConfiguration<SuppliersListOption>>;

  #popToast = {
    CREATE_SUCCESS: () =>
      this.toastService.success({
        id: 'create_field_success',
        message: 'notifications.suppliers.create.success',
      }),
    CREATE_FAILURE: (reason = 'Not specified') =>
      this.toastService.error({
        id: 'create_field_failure',
        message: 'notifications.suppliers.create.failure',
        interpolatedParams: { reason },
      }),
    UPDATE_SUCCESS: () =>
      this.toastService.success({
        id: 'update_field_success',
        message: 'notifications.suppliers.update.success',
      }),
    UPDATE_FAILURE: () =>
      this.toastService.error({
        id: 'update_field_failure',
        message: 'notifications.suppliers.update.failure',
      }),
  };

  readonly filtersConfig: DynamicFilterInput[] = [
    {
      id: 'keyword',
      label: 'common.suppliers.table.filter',
      format: ValueFormat.AS_IS,
      filters: {
        objectPath: 'name',
        validFormat: 'keyword',
        type: FilterType.MASTER_TEXT,
      },
      scope: ['other-suppliers'],
    },
  ];

  filters = new FormGroup({
    keyword: new FormControl<string>(''),
  });
  #destroy$ = new Subject<void>();
  headerActions$: Observable<ActionContextLess[]> = this.permissionService
    .verifyWithOwnedPermissions$([Permission.CREATE_SUPPLIERS])
    .pipe(
      map((hasPermission) =>
        hasPermission
          ? [
              {
                key: 'add',
                label: 'common.global.add',
                color: ButtonColorsEnum.PRIMARY,
                e2eId: 'suppliers-create',
              },
            ]
          : [],
      ),
    );

  handleAction($event: { key: string; item?: SuppliersListOption }) {
    switch ($event.key) {
      case 'add':
        this.popCreateSupplierDialog();
        return;
    }

    if (!$event.item) {
      return;
    }
    switch ($event.key) {
      case 'edit':
        this.popEditSupplierDialog($event.item);
        break;
      case 'delete':
        this.popDeleteSupplierDialog($event.item);
        break;
      default:
        console.warn(`Action ${$event.key} is not supported`);
        break;
    }
  }

  popCreateSupplierDialog = () => {
    const dialogRef = this.matDialog.open(SuppliersCreateEditDialogComponent, {
      data: { edit: false },
    });
    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((supplier: SuppliersListOption) => !!supplier),
        switchMap((supplier: SuppliersListOption) => {
          const payload: SupplierPostRequest = {
            ...supplier,
            groups: supplier.groups?.map(({ uuid }) => uuid),
            users: supplier.users?.map(({ uuid }) => uuid),
          };
          return this.supplierService.postSupplier(payload).pipe(tap(() => this.#popToast.CREATE_SUCCESS()));
        }),
      )
      .subscribe({
        next: () => {
          this.suppliersDataSource.applyFilter(this.suppliersDataSource.text$?.value ?? '');
        },
        error: (err) => {
          this.#popToast.CREATE_FAILURE();
          throw err;
        },
      });
  };

  popDeleteSupplierDialog(data: SuppliersListOption) {
    this.deleteDialog
      .openDelete()
      .pipe(
        filter(Boolean),
        switchMap(() => this.supplierService.deleteSupplier(data.uuid)),
      )
      .subscribe({
        next: () => {
          this.#popToast.UPDATE_SUCCESS();
          this.refreshService.refresh();
        },
      });
  }

  popEditSupplierDialog = (supplierData: SuppliersListOption) => {
    const dialogRef = this.dialogPageHost.open(SuppliersCreateEditDialogComponent, {
      data: {
        supplier: supplierData,
        edit: true,
        asPage: true,
        // "Suppliers" carries no path: this *is* that page, so a router link would be a same-URL
        // navigation and the reused component would leave the panel open.
        breadcrumbs: [{ name: 'Home', path: '/' }, { name: 'pages.home.menu.suppliers' }],
      },
    });
    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((supplier: SupplierEditDialogResult) => !!supplier),
        withLatestFrom(this.quarantineService.canManageQuarantineConfigs$),
        map(([supplier, canManageQuarantineConfigs]) => {
          // The editor returns assigned users as uuids alongside the details, in one payload --
          // there is no second dialog to reconcile with any more (#16593). `configChanged` stays
          // behind: it tells us whether to call the quarantine endpoint, it is not request data.
          const { configChanged, ...payload } = supplier;

          let quarantineRules: QuarantineConfigRules | null = null;

          if (canManageQuarantineConfigs) {
            if (configChanged) {
              quarantineRules = QUARANTINE_ALL_ASSET_TYPES.reduce((acc, type) => {
                acc[type] = supplier?.quarantine?.includes(type) ?? false;
                return acc;
              }, {} as QuarantineConfigRules);
            }
          }

          return { payload, quarantineRules };
        }),
        switchMap(({ payload, quarantineRules }) => {
          const saveSupplier$ = this.supplierService.patchSupplier(supplierData.uuid, payload);
          const saveQuarantine$ = quarantineRules
            ? this.quarantineService.saveSupplierQuarantineConfig(payload?.uuid, quarantineRules)
            : EMPTY;

          return concat(saveSupplier$, saveQuarantine$).pipe(
            tap(() => {
              this.#popToast.UPDATE_SUCCESS();
              this.refreshService.refresh();
            }),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.suppliersDataSource.applyFilter(this.suppliersDataSource.text$?.value ?? '');
        },
        error: (err) => {
          this.#popToast.UPDATE_FAILURE();
          this.refreshService.refresh();
          throw err;
        },
      });
  };

  ngOnInit() {
    this.suppliersDataSource.applyFilter('');
    this.filters.valueChanges.pipe(debounceTime(250), takeUntil(this.#destroy$)).subscribe((value) => {
      this.suppliersDataSource.applyFilter(value.keyword ?? '', ['name']);
    });

    this.viewConfiguration$ = combineLatest([
      this.permissionService.verifyWithOwnedPermissions$([Permission.EDIT_SUPPLIERS]),
      this.permissionService.verifyWithOwnedPermissions$([Permission.DELETE_SUPPLIERS]),
      this.superAdminAccessService.isSuperAdmin$,
    ]).pipe(
      map(([canEdit, canDelete, isSuperAdmin]) => {
        return {
          tableAdvanced: {
            actions: [
              {
                key: 'edit',
                icon: 'edit',
                onDoubleClick: false,
                label: 'common.global.edit',
                hiddenIf: () => !canEdit,
                disabledIf: (item: SuppliersListOption) => !isSuperAdmin && !!item.is_global,
                e2eId: 'suppliers-edit',
              },
              {
                key: 'delete',
                icon: 'delete',
                label: 'common.global.delete',
                hiddenIf: () => !canDelete,
                disabledIf: (item: SuppliersListOption) => !isSuperAdmin && !!item.is_global,
                fragile: true,
                e2eId: 'suppliers-delete',
              },
            ],
            columnsEnabled: this.enabled,
            columns: [
              {
                id: 'emails',
                label: 'Emails',
                valuePath: 'emails',
                foldValues: true,
                viewFormat: {
                  maxVisibleValues: 1,
                },
              },
              {
                id: 'name',
                label: 'Supplier name',
                valuePath: 'name',
              },
              {
                id: 'facility_of_origin',
                label: 'Facility of origin',
                valuePath: 'facility_of_origin',
                viewFormat: {
                  fallback: 'N/A',
                },
              },
              {
                id: 'is_global',
                label: 'Global supplier',
                valuePath: 'is_global',
                viewFormat: {
                  modifiers: {
                    yesNo: true,
                  },
                },
              },
              {
                id: 'created_by',
                label: 'Created by',
                valuePath: 'created_by',
                sortable: true,
                // Server-side sort key (GET /suppliers order-by header), not a client object path.
                sortObjectPath: 'supplierCreatedBy.displayName',
                viewFormat: {
                  modifiers: { valueObjectKey: 'name' },
                  // created_by == null → "Unknown"
                  fallback: 'Unknown',
                },
              },
              {
                id: 'created_at',
                label: 'Created date',
                valuePath: 'created_at',
                sortable: true,
                sortObjectPath: 'supplier.createdAt',
                viewFormat: {
                  modifiers: { dateFormat: 'date-time' },
                  fallback: '',
                },
              },
              {
                id: 'modified_by',
                label: 'Modified by',
                valuePath: 'modified_by',
                sortable: true,
                sortObjectPath: 'supplierModifiedBy.displayName',
                viewFormat: {
                  modifiers: { valueObjectKey: 'name' },
                  // modified_by == null (no edit yet) → blank
                  fallback: '',
                },
              },
              {
                id: 'modified_at',
                label: 'Modified date',
                valuePath: 'modified_at',
                sortable: true,
                // Backend maps the "modified" audit field to updatedAt.
                sortObjectPath: 'supplier.updatedAt',
                viewFormat: {
                  modifiers: { dateFormat: 'date-time' },
                  fallback: '',
                },
              },
              {
                id: 'actions',
                type: 'actions',
              },
            ],
          },
        };
      }),
    );
  }

  ngOnDestroy() {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
